// src/services/crimePreventionService.js
import { fetchWithAuth } from "../utils/fetchWithAuth";

const BASE = "/crime-prevention";

/* ===================== Documentos (PDF) ===================== */

export const listCPDocs = async () => {
  const res = await fetchWithAuth(`${BASE}`, { method: "GET" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`GET ${BASE} ${res.status} ${res.statusText} ${text}`.trim());
  }
  return res.json();
};

export const uploadCPDoc = async ({ file, title, versionTag, isPublished = true }) => {
  if (!file) throw new Error("Archivo requerido");
  const fd = new FormData();
  fd.append("file", file, file.name || "mpd.pdf");
  if (title) fd.append("title", String(title).trim());
  if (versionTag) fd.append("versionTag", String(versionTag).trim());
  fd.append("isPublished", String(!!isPublished));

  const res = await fetchWithAuth(`${BASE}/upload`, { method: "POST", body: fd });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`POST ${BASE}/upload ${res.status} ${res.statusText} ${text}`.trim());
  }
  return res.json();
};

export const updateCPDoc = async (id, payload = {}) => {
  const res = await fetchWithAuth(`${BASE}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`PATCH ${BASE}/${id} ${res.status} ${res.statusText} ${text}`.trim());
  }
  return res.json();
};

export const deleteCPDoc = async (id) => {
  const res = await fetchWithAuth(`${BASE}/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`DELETE ${BASE}/${id} ${res.status} ${res.statusText} ${text}`.trim());
  }
  return true;
};

export const downloadCPDocUrl = (id) =>
  id ? `${BASE}/${encodeURIComponent(id)}/download` : "#";

export const getLatestCPDoc = async () => {
  const res = await fetchWithAuth(`${BASE}/latest`, { method: "GET" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`GET ${BASE}/latest ${res.status} ${res.statusText} ${text}`.trim());
  }
  return res.json();
};

/* ===================== Config pública (encabezados + visible) ===================== */

export const getCPConfig = async () => {
  const res = await fetchWithAuth(`${BASE}/config`, { method: "GET" });
  if (!res.ok) throw new Error(`GET ${BASE}/config ${res.status}`);
  const cfg = await res.json();

  // Normaliza null/undefined del backend
  return {
    pageTitle: cfg?.pageTitle ?? "Modelo de Prevención del Delito",
    subtitle: cfg?.subtitle ?? "",
    principlesVisible: !!cfg?.principlesVisible,
    // ⬇ texto libre que escribes (e.g., "Octubre 2025")
    lastReview: cfg?.lastReview ?? "",
    // ⬇ fecha ISO (o null) que se muestra si hay docs visibles
    lastVisibleAt: cfg?.lastVisibleAt ?? null,
  };
};

export const updateCPConfig = async (cfg = {}) => {
  const payload = {};

  if (cfg.pageTitle != null) payload.pageTitle = String(cfg.pageTitle);
  if (cfg.subtitle != null) payload.subtitle = String(cfg.subtitle);
  if (cfg.principlesVisible != null) payload.principlesVisible = !!cfg.principlesVisible;

  // ⬇ Enviar el TEXTO tal cual
  if (cfg.lastReview !== undefined) payload.lastReview = String(cfg.lastReview ?? "");

  // ⬇ Manejo de la fecha visible (ISO o null)
  if (cfg.lastVisibleAt !== undefined) {
    if (cfg.lastVisibleAt === null || cfg.lastVisibleAt === "") {
      payload.lastVisibleAt = null;
    } else {
      const dt = new Date(cfg.lastVisibleAt);
      if (!isNaN(dt.getTime())) payload.lastVisibleAt = dt.toISOString();
    }
  }

  const res = await fetchWithAuth(`${BASE}/config`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`PUT ${BASE}/config ${res.status} ${res.statusText} ${text}`.trim());
  }
  return res.json();
};

/* ===================== Contenido público extendido ===================== */

export const getCPContent = async () => {
  try {
    const res = await fetchWithAuth(`${BASE}/content`, { method: "GET" });
    if (res.ok) return await res.json();
  } catch {}
  try {
    const res2 = await fetch("/cp-content.json", { cache: "no-cache" });
    if (res2.ok) return await res2.json();
  } catch {}
  return null;
};

export const updateCPContent = async (payload) => {
  // Solo enviamos campos permitidos por el DTO del backend
  const clean = {};
  if (payload?.lastReview != null) clean.lastReview = String(payload.lastReview);
  if (Array.isArray(payload?.principles)) clean.principles = payload.principles;
  if (Array.isArray(payload?.sections)) clean.sections = payload.sections;

  const res = await fetchWithAuth(`${BASE}/content`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(clean),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`PUT ${BASE}/content ${res.status} ${res.statusText} ${text}`.trim());
  }
  return res.json();
};
